import React, { useState } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Building2, ChevronRight, Users } from 'lucide-react';

const WorkspaceSelector = () => {
  const { workspaces, selectWorkspace } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const handleSelectWorkspace = async (workspaceId) => {
    setLoading(true);
    setSelectedId(workspaceId);
    
    try {
      const currentWorkspace = await selectWorkspace(workspaceId);
      toast.success('Workspace selected successfully!');
      
      // Navigate based on role
      if (currentWorkspace) {
        if (currentWorkspace.role === 'auditor') {
          navigate('/auditor-dashboard');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select workspace');
      setLoading(false);
      setSelectedId(null);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl border-0 shadow-2xl">
        <CardHeader className="text-center pb-6">
          <div className="w-16 h-16 bg-navy-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-slate-900 mb-2">
            Select Workspace
          </CardTitle>
          <CardDescription className="text-base">
            You belong to multiple workspaces. Choose which one to access.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-3">
            {workspaces.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => handleSelectWorkspace(workspace.id)}
                disabled={loading}
                className="w-full text-left p-4 rounded-xl border-2 border-slate-200 hover:border-navy-500 hover:bg-emerald-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center group-hover:from-emerald-100 group-hover:to-emerald-200 transition-colors">
                      <Building2 className="w-6 h-6 text-blue-600 group-hover:text-navy-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 group-hover:text-navy-700">
                        {workspace.name}
                      </h3>
                      <p className="text-sm text-slate-600 capitalize">
                        Role: {workspace.role}
                      </p>
                    </div>
                  </div>
                  
                  {loading && selectedId === workspace.id ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-navy-600"></div>
                  ) : (
                    <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-navy-600 transition-colors" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkspaceSelector;
